import classnames from 'classnames';
import { groupBy, prop } from 'ramda';

import { replaceAccents } from '../../utils/string';

import styles from './Select.module.scss';

import type { BaseOption, InternalSelectOption, SelectOption } from './Select.types';

type ParseOptionsConfig = {
    options: BaseOption[];
    className?: string;
    mapOption?: (
        option: BaseOption,
        defaultMapFn: (option: BaseOption) => InternalSelectOption
    ) => InternalSelectOption;
};

type FlattenedSelectOption = Omit<SelectOption, 'options'>;

const stringOrUndefined = (value: unknown) => {
    return typeof value === 'string' ? value : undefined;
};

export const getLabelText = ({ label, value, key, title }: BaseOption) => {
    return [label, title, value, key].map(stringOrUndefined).find(Boolean);
};

const parseOption = (option: BaseOption): InternalSelectOption => {
    const {
        value,
        label,
        key,
        render,
        id,
        title,
        renderer,
        className: optionClassName,
        ...rest
    } = option;
    const result = {
        value: value ?? key ?? id,
        label: render ?? label ?? title ?? value ?? key,
        __searchableLabel: getLabelText({ label, value, key, title }),
        'aria-label': typeof label === 'string' ? label : value,
        key: key ?? id,
        className: classnames(styles.option, optionClassName),
        id,
        title,
        ...rest,
        original: option
    };
    return {
        ...result,
        label: renderer ? renderer(result) : result.label
    };
};

export const isOptionGroup = (option: BaseOption) => {
    return 'options' in option && Array.isArray(option.options);
};

const mapOptions = (options: BaseOption[], mapFn?: ParseOptionsConfig['mapOption']) => {
    return options?.length > 0
        ? options.map((opt) =>
              typeof mapFn === 'function' ? mapFn(opt, parseOption) : parseOption(opt)
          )
        : [];
};

const parseWithCategoryAttribute = (config: ParseOptionsConfig): InternalSelectOption[] => {
    const { options, mapOption } = config;
    const optionsByCategory = groupBy(
        prop('category'),
        options as (BaseOption & { category: string })[]
    );
    const result = Object.keys(optionsByCategory).map(
        (category) =>
            mapOptions([
                {
                    key: category,
                    label: category,
                    options: mapOptions(optionsByCategory[category]!, mapOption),
                    original: {} as BaseOption
                }
            ])[0]
    );
    return result;
};

const parseWithGroups = (config: ParseOptionsConfig): InternalSelectOption[] => {
    const { options: groups, mapOption } = config;
    return groups.map((group) => {
        const { options, ...rest } = group;
        if (!options) return mapOptions([rest], mapOption)[0];
        return {
            options: mapOptions(options as BaseOption[], mapOption),
            original: group,
            ...rest
        };
    });
};

const hasCategoryAttribute = (options: BaseOption[]) => options.some((option) => option.category);

const hasGroups = (options: BaseOption[]) => options.some((option) => option.options);

export const getOptionIdentifier = (option: BaseOption, fieldValueKey: keyof SelectOption) =>
    (option[fieldValueKey] ?? option.key ?? option.id ?? option.value) as string | undefined;

export const matchText = (text: string, searchValue: string) => {
    const normalizeText = replaceAccents(text)?.toLowerCase();
    const normalizeSearch = replaceAccents(searchValue)?.toLowerCase();
    return normalizeText?.includes(normalizeSearch);
};

export const flattenOptions = <
    OptionType extends {
        options?: BaseOption[];
    }
>(
    options: OptionType[],
    { includeGroups }: { includeGroups?: boolean } = {}
) => {
    if (!hasGroups(options)) {
        return options;
    }
    return options.reduce((acc, option) => {
        if (option.options) {
            return [
                ...acc,
                ...(includeGroups ? [option as OptionType] : []),
                ...(option.options as OptionType[])
            ];
        }
        return [...acc, option];
    }, [] as OptionType[]);
};

export const mapOptionsToIdentifiers = (
    options: FlattenedSelectOption[],
    fieldValueKey: string | keyof SelectOption
) => {
    return options.map((option) => getOptionIdentifier(option, fieldValueKey)) as string[];
};

export const parseOptions = (config: ParseOptionsConfig): InternalSelectOption[] => {
    const { options, mapOption } = config;
    if (hasCategoryAttribute(options)) {
        return parseWithCategoryAttribute(config);
    }
    if (hasGroups(options)) {
        return parseWithGroups(config);
    }
    return mapOptions(options, mapOption);
};

/**
 * Check if the current focused element is inside the floating element or the select container.
 */
export const isFocusTrapped = (
    floatingRefs: {
        floating: React.RefObject<HTMLElement | null>;
        domReference: React.RefObject<HTMLElement | null>;
    },
    activeElement: Element | null
) => {
    // check if the current active element is inside the floating element
    if (
        (floatingRefs.floating.current && floatingRefs.floating.current.contains(activeElement)) ||
        activeElement === floatingRefs.floating.current
    ) {
        return true;
    }
    // check if the current active element is inside the trigger container
    if (
        floatingRefs.domReference.current &&
        floatingRefs.domReference.current.contains(activeElement)
    ) {
        return true;
    }
    return false;
};

export const isStringOrNumber = (v: unknown): v is string | number =>
    typeof v === 'string' || typeof v === 'number';
